import React, { useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getFlightsFromResponse,
  getFareFromResponse,
  getRouteFromSegments,
  formatDateTime,
} from "../../utils/flightBookingUtils";
import axios from "axios";
import html2pdf from "html2pdf.js";
import { FaTimes } from "react-icons/fa";

// Logo from public (same as favicon) so it works in PDF
const TICKET_LOGO = "/celestiallogowbg1.png";

const CONTACT_EMAIL = "celestialtours.ct@gmail.com";
const CONTACT_MOBILE = "+91 8839725353";
const INDIGO_HELP = "0124-6173838, 0124-4973838";

const calculateSSR = (resOn, resRet) => {
  let meals = 0;
  let baggage = 0;
  let seats = 0;

  const calculateNode = (node) => {
    if (!node) return;
    if (node.Meal && Array.isArray(node.Meal)) {
      meals += node.Meal.reduce((sum, m) => sum + (Number(m.mealAmt) || 0), 0);
    }
    if (node.Bagg && Array.isArray(node.Bagg)) {
      baggage += node.Bagg.reduce((sum, b) => sum + (Number(b.baggAmt) || 0), 0);
    }
    if (node.Seat && Array.isArray(node.Seat)) {
      seats += node.Seat.reduce((sum, s) => sum + (Number(s.seatAmt) || 0), 0);
    }
  };

  calculateNode(resOn?.ssrinfo?.Onward);
  calculateNode(resOn?.ssrinfo?.Return);
  calculateNode(resOn?.Ssrinfo?.Onward);
  calculateNode(resOn?.Ssrinfo?.Return);
  
  if (resRet) {
    calculateNode(resRet?.ssrinfo?.Onward);
    calculateNode(resRet?.ssrinfo?.Return);
    calculateNode(resRet?.Ssrinfo?.Onward);
    calculateNode(resRet?.Ssrinfo?.Return);
  }

  return { meals, baggage, seats };
};

const FareBreakupSection = ({ resOn, resRet, isRoundTrip, onwardB, returnB }) => {
  const fareOn = getFareFromResponse(resOn);
  const fareRet = resRet ? getFareFromResponse(resRet) : null;
  const fareOnNum = fareOn != null ? (typeof fareOn === "number" ? fareOn : parseFloat(fareOn)) : 0;
  const fareRetNum = fareRet != null ? (typeof fareRet === "number" ? fareRet : parseFloat(fareRet)) : 0;
  
  let ftdTotal = isRoundTrip ? fareOnNum + fareRetNum : fareOnNum;
  let baseFare = Math.round(ftdTotal * 0.81);
  let tax = ftdTotal - baseFare;
  
  const { meals, baggage, seats } = calculateSSR(resOn, resRet);
  let ssrTotal = meals + baggage + seats;
  
  if (onwardB?.isCustomFlight && onwardB?.customFlightDetails?.fare) {
    const f = onwardB.customFlightDetails.fare;
    baseFare = Number(f.baseFare) || 0;
    tax = Number(f.taxes) || 0;
    ftdTotal = baseFare + tax;
    if (f.ssrTotal) {
      ssrTotal = Number(f.ssrTotal);
    }
  }
  
  const amountPaidOn = onwardB?.amount || 0;
  const amountPaidRet = returnB?.amount || 0;
  const totalPaid = amountPaidOn + amountPaidRet;
  
  // Ensure the grand total is exactly what was paid via Razorpay, falling back to ftdTotal + ssrTotal only if missing
  const grandTotal = totalPaid > 0 ? totalPaid : (ftdTotal + ssrTotal);

  let remainingCharges = grandTotal - ftdTotal - ssrTotal;
  if (remainingCharges < 0) remainingCharges = 0;
  
  return (
    <div style={{ borderTop: "2px solid #fb923c", paddingTop: "12px", marginTop: "12px" }}>
      <div style={{ fontSize: "0.875rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}><span>Base Fare</span><span>{baseFare.toLocaleString("en-IN")} INR</span></div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}><span>Tax & Surcharges</span><span>{tax.toLocaleString("en-IN")} INR</span></div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontWeight: "600" }}><span>Total Fare</span><span>{ftdTotal.toLocaleString("en-IN")} INR</span></div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}><span>Meals</span><span>{meals > 0 ? meals.toLocaleString("en-IN") : "0"} INR</span></div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}><span>Baggage</span><span>{baggage > 0 ? baggage.toLocaleString("en-IN") : "0"} INR</span></div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}><span>Seats</span><span>{seats > 0 ? seats.toLocaleString("en-IN") : "0"} INR</span></div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}><span>Other Services / Fees</span><span>{remainingCharges > 0 ? remainingCharges.toLocaleString("en-IN") : "0"} INR</span></div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px", paddingTop: "12px", borderTop: "2px solid #ea580c", background: "#fff7ed", marginLeft: "-8px", marginRight: "-8px", padding: "8px", borderRadius: "4px", fontWeight: "bold", color: "#9a3412" }}>
        <span>Grand Total</span>
        <span>{grandTotal.toLocaleString("en-IN")} INR</span>
      </div>
    </div>
  );
};

export default function FlightTicketPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const ticketRef = useRef(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [sendWithoutPrice, setSendWithoutPrice] = useState(false);
  const [hideFareInPdf, setHideFareInPdf] = useState(false);

  const viewItem = state?.viewItem;
  if (!viewItem) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <p className="text-gray-600 mb-4">No ticket data. Please open the ticket from the bookings list.</p>
          <button onClick={() => navigate("/admin/booking")} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Back to Bookings</button>
        </div>
      </div>
    );
  }

  const isGroupedRT = viewItem.type === "roundtrip";
  const onwardB = isGroupedRT ? viewItem.onward : viewItem.booking;
  const returnB = isGroupedRT ? viewItem.return : null;
  const booking = onwardB;

  const resOn = onwardB?.response || {};
  const resRet = returnB?.response || {};
  let onwardFlights = getFlightsFromResponse(resOn, "onward");
  let returnFlights = isGroupedRT 
    ? getFlightsFromResponse(resRet, "onward") 
    : getFlightsFromResponse(resOn, "return");
  let routeOn = getRouteFromSegments(onwardFlights);
  let routeRet = getRouteFromSegments(returnFlights);

  if (onwardB?.isCustomFlight && onwardB?.customFlightDetails) {
    routeOn = { dep: onwardB.customFlightDetails.from, arr: onwardB.customFlightDetails.to };
    if (onwardB.customFlightDetails.returnDate) {
      routeRet = { dep: onwardB.customFlightDetails.to, arr: onwardB.customFlightDetails.from };
    }
    const customSegments = onwardB.customFlightDetails.segments || [];
    const mapCustomSegment = (s) => {
      let depRaw = String(s.departureTime || "").replace(/\D/g, "");
      let arrRaw = String(s.arrivalTime || "").replace(/\D/g, "");
      return {
        depCName: s.from || onwardB.customFlightDetails.from || "",
        depCode: "",
        arrCName: s.to || onwardB.customFlightDetails.to || "",
        arrCode: "",
        flightNumber: s.flightNo || "",
        flightNo: s.flightNo || "",
        airlineName: s.airline || "",
        airName: s.airline || "",
        depDate: depRaw,
        arrDate: arrRaw,
        depTime: "",
        arrTime: "",
        duration: s.duration || "",
        layovers: s.layovers || []
      };
    };
    onwardFlights = customSegments.filter(s => s.direction === 'onward').map(mapCustomSegment);
    returnFlights = customSegments.filter(s => s.direction === 'return').map(mapCustomSegment);
    if (onwardFlights.length === 0 && customSegments.length > 0) {
       onwardFlights = customSegments.map(mapCustomSegment);
    }
  }
  
  const isRoundTrip = isGroupedRT || returnFlights.length > 0;

  const fareOn = getFareFromResponse(resOn);
  const fareRet = returnB ? getFareFromResponse(resRet) : null;
  const fareOnNum = fareOn != null ? (typeof fareOn === "number" ? fareOn : parseFloat(fareOn)) : (onwardB?.amount || 0);
  const fareRetNum = fareRet != null ? (typeof fareRet === "number" ? fareRet : parseFloat(fareRet)) : (returnB?.amount || 0);
  const totalFare = isRoundTrip ? fareOnNum + fareRetNum : fareOnNum;

  const pnrOn = resOn?.Ticket?.Onward?.passenger || resOn?.Ticket?.passenger || resOn?.ticket?.Onward?.passenger || resOn?.ticket?.passenger || [];
  const pnrRet = returnB ? (resRet?.Ticket?.Onward?.passenger || resRet?.Ticket?.passenger || resRet?.ticket?.Onward?.passenger || resRet?.ticket?.passenger || []) : [];
  const pnrList = [...(Array.isArray(pnrOn) ? pnrOn : pnrOn ? [pnrOn] : []), ...(Array.isArray(pnrRet) ? pnrRet : pnrRet ? [pnrRet] : [])];
  let pnr = pnrList[0]?.pnr || pnrList[0] || booking.refID || onwardB.refID || "—";
  if (onwardB?.isCustomFlight && onwardB?.customFlightDetails?.pnr) {
    pnr = onwardB.customFlightDetails.pnr;
  }

  const bookingId = onwardB.bookingID || onwardB.refID || onwardB.ftdBookingID || "—";
  const bookedOn = booking.createdAt ? new Date(booking.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
  const passengers = booking.passenger || [];
  const status = (booking.bookingStatus || "Confirmed").toUpperCase();
  let routeStr = routeOn ? `${routeOn.depCode || routeOn.dep}-${routeOn.arrCode || routeOn.arr}` : "—";
  if (isRoundTrip && routeRet) {
    routeStr += ` | ${routeRet.depCode || routeRet.dep}-${routeRet.arrCode || routeRet.arr}`;
  }

  const getPdfBlob = () =>
    new Promise((resolve, reject) => {
      const el = ticketRef.current;
      if (!el) {
        reject(new Error("Ticket content not found"));
        return;
      }
      const opt = {
        margin: [8, 8, 8, 8],
        filename: `Celestial-Ticket-${bookingId}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };
      html2pdf().set(opt).from(el).outputPdf("blob").then(resolve).catch(reject);
    });

  const handleDownloadPDF = async () => {
    setPdfGenerating(true);
    try {
      const blob = await getPdfBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Celestial-Ticket-${bookingId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert(e?.message || "Failed to generate PDF. Try again.");
    } finally {
      setPdfGenerating(false);
    }
  };

  const openEmailModal = () => {
    setEmailTo(booking?.email || "");
    setEmailSent(false);
    setEmailModalOpen(true);
  };

  const handleEmailTicket = async () => {
    const to = (emailTo || "").trim();
    if (!to) {
      alert("Please enter an email address.");
      return;
    }
    setSendingEmail(true);
    try {
      let blob;
      if (sendWithoutPrice) {
        setHideFareInPdf(true);
        await new Promise((r) => setTimeout(r, 200));
        blob = await getPdfBlob();
        setHideFareInPdf(false);
      } else {
        blob = await getPdfBlob();
      }
      const formData = new FormData();
      formData.append("to", to);
      formData.append("subject", `Your flight e-ticket - ${bookingId}`);
      formData.append("pdf", blob, `Celestial-Ticket-${bookingId}.pdf`);
      const BASE = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000";
      await axios.post(`${BASE}/api/booking/send-ticket-email`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEmailSent(true);
      setEmailModalOpen(false);
    } catch (e) {
      alert(e.response?.data?.message || e.message || "Failed to send email.");
    } finally {
      setSendingEmail(false);
      setHideFareInPdf(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Top actions - not in print */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4 print:hidden">
          <button onClick={() => navigate("/admin/booking")} className="text-blue-600 hover:underline font-medium">← Back to Bookings</button>
          <div className="flex gap-3">
            <button onClick={handleDownloadPDF} disabled={pdfGenerating} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">
              {pdfGenerating ? "Generating PDF…" : "Download PDF"}
            </button>
            <button onClick={openEmailModal} className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium">
              Send to email
            </button>
          </div>
        </div>

        {/* Email modal: enter email and send ticket PDF */}
        {emailModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 print:hidden" onClick={() => !sendingEmail && setEmailModalOpen(false)}>
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900">Send ticket to email</h3>
                <button type="button" onClick={() => !sendingEmail && setEmailModalOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600">
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-3">Enter the email address where the ticket PDF should be sent.</p>
              <input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="email@example.com"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-transparent mb-4"
              />
              <label className="flex items-center gap-2 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendWithoutPrice}
                  onChange={(e) => setSendWithoutPrice(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">Send without price (PDF will not show fare breakdown)</span>
              </label>
              {emailSent && <p className="text-sm text-green-600 mb-2">Ticket sent successfully.</p>}
              <div className="flex gap-3">
                <button type="button" onClick={() => !sendingEmail && setEmailModalOpen(false)} className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50">Cancel</button>
                <button type="button" onClick={handleEmailTicket} disabled={sendingEmail || !emailTo.trim()} className="flex-1 py-2 rounded-lg bg-orange-500 text-white font-medium hover:bg-orange-600 disabled:opacity-50">
                  {sendingEmail ? "Sending…" : "Send PDF"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Ticket content - inline hex/rgb only so html2pdf (html2canvas) does not hit oklab error */}
        <div ref={ticketRef} style={{ minWidth: "280px", background: "#ffffff", overflow: "hidden" }}>
          {/* Header with logo */}
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", padding: "1.5rem", borderBottom: "2px solid #fb923c", background: "linear-gradient(to bottom, #ffffff, #fff7ed)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <img src={TICKET_LOGO} alt="Celestial Tours" style={{ height: "48px", width: "48px", objectFit: "contain" }} />
              <div>
                <h1 style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#f97316" }}>Celestial Tours</h1>
                <p style={{ fontFamily: "monospace", fontWeight: "600", color: "#1f2937", marginTop: "2px" }}>Booking ID: {bookingId}</p>
                <p style={{ fontSize: "0.875rem", color: "#6b7280" }}>Booked on: {bookedOn}</p>
              </div>
            </div>
            <div style={{ textAlign: "right", fontSize: "0.875rem" }}>
              <p style={{ fontWeight: "600", color: "#1f2937" }}>Need help with your trip!</p>
              <p style={{ color: "#4b5563" }}>IndiGo {INDIGO_HELP}</p>
              <p style={{ color: "#374151" }}>{CONTACT_EMAIL} {CONTACT_MOBILE}</p>
            </div>
          </div>

          {/* Flight summary bar */}
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "1rem", padding: "1rem 1.5rem", background: "#2563eb", color: "#ffffff" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <span style={{ fontWeight: "bold" }}>IndiGo</span>
              <span style={{ fontFamily: "monospace", fontWeight: "600" }}>{routeStr}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <span style={{ fontFamily: "monospace", fontWeight: "bold" }}>{pnr}</span>
              <span style={{ padding: "4px 12px", background: "#22c55e", color: "#ffffff", borderRadius: "4px", fontWeight: "600", fontSize: "0.875rem" }}>{status}</span>
            </div>
          </div>

          {/* Flight Details | Retail Fare */}
          <div style={{ padding: "1.5rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "2px solid #fb923c", paddingBottom: "8px", marginBottom: "1rem" }}>
              <h2 style={{ fontSize: "1.125rem", fontWeight: "bold", color: "#111827" }}>Flight Details</h2>
              <span style={{ fontSize: "0.875rem", color: "#6b7280" }}>Retail Fare</span>
            </div>
            <h3 style={{ fontSize: "1.25rem", fontWeight: "bold", color: "#1f2937", marginBottom: "1rem" }}>{routeOn ? `${routeOn.dep} → ${routeOn.arr}` : "—"}</h3>

            {/* Onward segments */}
            {onwardFlights.map((flight, idx) => {
              const depDt = formatDateTime(flight.depDate, flight.depTime);
              const arrDt = formatDateTime(flight.arrDate, flight.arrTime);
              const duration = flight.duration || flight.flightDuration || flight.Duration || "—";
              return (
                <div key={`on-${idx}`} style={{ marginBottom: "1rem" }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-start" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontFamily: "monospace", fontWeight: "600" }}>{flight.flightNumber || flight.flightNo || "—"}</span>
                      <span style={{ fontSize: "0.875rem" }}>IndiGo</span>
                      <span style={{ color: "#6b7280", fontSize: "0.875rem" }}>{flight.aircraftType || "320"}</span>
                    </div>
                    <div style={{ fontSize: "0.875rem", color: "#16a34a", fontWeight: "500" }}>Economy · Partially Refundable</div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem", marginTop: "8px", padding: "1rem", background: "#f9fafb", borderRadius: "8px" }}>
                    <div>
                      <p style={{ fontSize: "0.75rem", color: "#6b7280", textTransform: "uppercase" }}>Departing</p>
                      <p style={{ fontWeight: "600" }}>{depDt.date} - {depDt.time}</p>
                      <p style={{ fontSize: "0.875rem", color: "#4b5563" }}>{flight.depCode}-{flight.depCName || flight.depCity || "—"}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: "0.75rem", color: "#6b7280", textTransform: "uppercase" }}>Arriving</p>
                      <p style={{ fontWeight: "600" }}>{arrDt.date} - {arrDt.time}</p>
                      <p style={{ fontSize: "0.875rem", color: "#4b5563" }}>{flight.arrCode}-{flight.arrCName || flight.arrCity || "—"}{flight.arrTerminal ? ` Terminal ${flight.arrTerminal}` : ""}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: "0.75rem", color: "#6b7280", textTransform: "uppercase" }}>Duration</p>
                      <p style={{ fontWeight: "600" }}>{duration} {duration.includes("mins") || duration.includes("h") ? "" : "mins"}</p>
                    </div>
                  </div>
                  {flight.layovers && flight.layovers.length > 0 && (
                    <div style={{ marginTop: "8px", padding: "12px", background: "#fef3c7", borderRadius: "8px", border: "1px solid #fde68a" }}>
                      <p style={{ fontSize: "0.875rem", fontWeight: "600", color: "#b45309", marginBottom: "4px" }}>Layover / Connection</p>
                      {flight.layovers.map((lo, li) => (
                        <div key={`on-layover-${idx}-${li}`} style={{ fontSize: "0.875rem", color: "#92400e" }}>
                          • {lo.airport} - {lo.duration} {lo.terminal ? `(${lo.terminal})` : ""}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {isRoundTrip && returnFlights.length > 0 && (
              <>
                <div style={{ borderTop: "2px dashed #d1d5db", margin: "1.5rem 0" }}></div>
                <h3 style={{ fontSize: "1.25rem", fontWeight: "bold", color: "#1f2937", marginBottom: "1rem" }}>{routeRet ? `${routeRet.dep} → ${routeRet.arr}` : "—"}</h3>

                {returnFlights.map((flight, idx) => {
                  const depDt = formatDateTime(flight.depDate, flight.depTime);
                  const arrDt = formatDateTime(flight.arrDate, flight.arrTime);
                  const duration = flight.duration || flight.flightDuration || flight.Duration || "—";
                  return (
                    <div key={`ret-${idx}`} style={{ marginBottom: "1rem" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-start" }}>
                        <span style={{ fontFamily: "monospace", fontWeight: "600" }}>{flight.flightNumber || flight.flightNo || "—"}</span>
                        <span style={{ fontSize: "0.875rem" }}>IndiGo</span>
                        <span style={{ fontSize: "0.875rem", color: "#16a34a", fontWeight: "500" }}>Economy · Partially Refundable</span>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem", marginTop: "8px", padding: "1rem", background: "#f9fafb", borderRadius: "8px" }}>
                        <div>
                          <p style={{ fontSize: "0.75rem", color: "#6b7280", textTransform: "uppercase" }}>Departing</p>
                          <p style={{ fontWeight: "600" }}>{depDt.date} - {depDt.time}</p>
                          <p style={{ fontSize: "0.875rem", color: "#4b5563" }}>{flight.depCode}-{flight.depCName || flight.depCity || "—"}</p>
                        </div>
                        <div>
                          <p style={{ fontSize: "0.75rem", color: "#6b7280", textTransform: "uppercase" }}>Arriving</p>
                          <p style={{ fontWeight: "600" }}>{arrDt.date} - {arrDt.time}</p>
                          <p style={{ fontSize: "0.875rem", color: "#4b5563" }}>{flight.arrCode}-{flight.arrCName || flight.arrCity || "—"}</p>
                        </div>
                        <div>
                          <p style={{ fontSize: "0.75rem", color: "#6b7280", textTransform: "uppercase" }}>Duration</p>
                          <p style={{ fontWeight: "600" }}>{duration} {duration.includes("mins") || duration.includes("h") ? "" : "mins"}</p>
                        </div>
                      </div>
                      {flight.layovers && flight.layovers.length > 0 && (
                        <div style={{ marginTop: "8px", padding: "12px", background: "#fef3c7", borderRadius: "8px", border: "1px solid #fde68a" }}>
                          <p style={{ fontSize: "0.875rem", fontWeight: "600", color: "#b45309", marginBottom: "4px" }}>Layover / Connection</p>
                          {flight.layovers.map((lo, li) => (
                            <div key={`ret-layover-${idx}-${li}`} style={{ fontSize: "0.875rem", color: "#92400e" }}>
                              • {lo.airport} - {lo.duration} {lo.terminal ? `(${lo.terminal})` : ""}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Passenger Details */}
          <div style={{ padding: "0 1.5rem 1.5rem" }}>
            <div style={{ borderTop: "2px solid #fb923c", paddingTop: "1rem", marginTop: "8px" }}>
              <h2 style={{ fontSize: "1.125rem", fontWeight: "bold", color: "#111827", marginBottom: "0.75rem" }}>Passenger Details</h2>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", fontSize: "0.875rem", border: "1px solid #e5e7eb", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f3f4f6" }}>
                      <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e5e7eb" }}>#</th>
                      <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e5e7eb" }}>Name, Passport & FF</th>
                      <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e5e7eb" }}>Sector PNR (Ticket Number)</th>
                      <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e5e7eb" }}>Barcode</th>
                      <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e5e7eb" }}>Baggage, Meals, Seat<br />Checkin | Cabin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {passengers.map((p, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "8px" }}>{i + 1}</td>
                        <td style={{ padding: "8px" }}>
                          <span style={{ fontWeight: "500" }}>{p.title} {p.fName} {p.lName} ({(p.pType || "A").toUpperCase()})</span>
                          {p.DOB && <p style={{ fontSize: "0.75rem", color: "#6b7280" }}>DOB: {p.DOB}</p>}
                          {p.passportNo && <p style={{ fontSize: "0.75rem", color: "#6b7280" }}>PP: {p.passportNo}</p>}
                          {p.Nationality && <p style={{ fontSize: "0.75rem", color: "#6b7280" }}>Nationality: {p.Nationality}</p>}
                        </td>
                        <td style={{ padding: "8px", fontFamily: "monospace" }}>
                          <div>{routeOn ? `${routeOn.depCode || routeOn.dep}-${routeOn.arrCode || routeOn.arr}` : "—"} {pnr} ({pnr})</div>
                          {isRoundTrip && routeRet && (
                            <div style={{ marginTop: "4px" }}>
                              {routeRet.depCode || routeRet.dep}-{routeRet.arrCode || routeRet.arr} {onwardB?.isCustomFlight && onwardB?.customFlightDetails?.returnPnr ? onwardB.customFlightDetails.returnPnr : pnr} ({onwardB?.isCustomFlight && onwardB?.customFlightDetails?.returnPnr ? onwardB.customFlightDetails.returnPnr : pnr})
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "8px" }}>—</td>
                        <td style={{ padding: "8px" }}>20 KG | 7 KG</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Traveller Information */}
            <div style={{ borderTop: "2px solid #fb923c", paddingTop: "1rem", marginTop: "1rem" }}>
              <h2 style={{ fontSize: "1.125rem", fontWeight: "bold", color: "#111827", marginBottom: "8px" }}>Traveller Information</h2>
              <p style={{ color: "#374151" }}>Email: {booking.email || "—"}</p>
              <p style={{ color: "#374151" }}>Mobile: {booking.mobile || "—"}</p>
            </div>

            {/* Fare Breakup - hidden in PDF when "Send without price" is used */}
            {!hideFareInPdf && (
              <div style={{ borderTop: "2px solid #fb923c", paddingTop: "1rem", marginTop: "1rem" }}>
                <h2 style={{ fontSize: "1.125rem", fontWeight: "bold", color: "#111827", marginBottom: "8px" }}>Fare Breakup</h2>
                <FareBreakupSection resOn={resOn} resRet={resRet} isRoundTrip={isRoundTrip} onwardB={onwardB} returnB={returnB} />
              </div>
            )}

            {/* Mandatory checklist */}
            <div style={{ borderTop: "2px solid #fb923c", paddingTop: "1rem", marginTop: "1rem" }}>
              <h2 style={{ fontSize: "1.125rem", fontWeight: "bold", color: "#111827", marginBottom: "8px" }}>Mandatory check-list for passengers</h2>
              <ol style={{ listStyle: "decimal", listStylePosition: "inside", color: "#374151", fontSize: "0.875rem", margin: 0, padding: 0 }}>
                <li>Complete your web check-in before arriving at the airport</li>
                <li>Report to the airport at least 3 hours prior to your flight departure</li>
              </ol>
            </div>

            {/* Terms & Conditions */}
            <div style={{ borderTop: "2px solid #fb923c", paddingTop: "1rem", marginTop: "1rem", fontSize: "0.75rem", color: "#4b5563" }}>
              <h2 style={{ fontSize: "0.875rem", fontWeight: "bold", color: "#111827", marginBottom: "8px" }}>Terms & Conditions</h2>
              <p>All Flight timings are shown in local timezones. Change in the Name or Title of the Passenger is not allowed. Carry Photo ID / Passport for Check-in. Customer to report to the Airport at least 2 hours (Domestic) and 3 hours (International) before departure. Use Airline PNR while talking to Airlines. Use Booking ID for all communication with Celestial Tours. Check the Baggage Allowance – Cabin and Check-in. For cancellation/date change, Airlines Fees & Celestial Tours Service Fees will apply. No-show tickets are non-refundable. For International Trips, ensure passport is valid for more than 6 months. Please check Transit & Destination Visa Requirement. Celestial Tours is not liable for any Discrepancy/Deficiency in service by the Airline. Any discrepancy regarding this ticket, please inform us within 3 hrs of Issuance.</p>
              <p style={{ fontWeight: "600", color: "#1f2937", marginTop: "8px" }}>!!! Have a Nice Trip !!!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
